import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createShowRealtimeClient } from '@fetmode/api-client';
import { resolveApiUrl, useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import type { StreamSignal } from '@fetmode/types';

const apiBaseUrl = resolveApiUrl(import.meta.env);

export type LiveStreamWait = {
  showId: string;
  username: string;
  displayName: string;
  notify: boolean;
  autoSwitch: boolean;
};

type LiveStreamWaitContextValue = {
  wait: LiveStreamWait | null;
  notifyArmed: boolean;
  startWait: (next: LiveStreamWait) => void;
  clearWait: () => void;
};

const LiveStreamWaitContext = createContext<LiveStreamWaitContextValue | null>(null);

export function roomPath(username: string): string {
  return `/@${username.replace(/^@/, '')}`;
}

export function isOnModelRoom(pathname: string, username: string): boolean {
  const handle = username.replace(/^@/, '').toLowerCase();
  const path = pathname.replace(/\/$/, '').toLowerCase();
  return path === `/@${handle}` || path === `/${handle}`;
}

export async function armNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') {
    return false;
  }
  if (Notification.permission === 'granted') {
    return true;
  }
  if (Notification.permission === 'denied') {
    return false;
  }
  const result = await Notification.requestPermission();
  return result === 'granted';
}

export function notifyStreamWait(wait: LiveStreamWait): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }
  const title = t('common.show.playerWaitingNotifyTitle').replace('{{name}}', wait.displayName);
  const body = t('common.show.playerWaitingNotifyBody');
  try {
    new Notification(title, {
      body,
      tag: `fm-live-${wait.username}`,
      icon: '/brand/logo.png',
    });
  } catch {
    // Embedded browsers may expose Notification but reject construction.
  }
}

export function LiveStreamWaitProvider({ children }: { children: ReactNode }): ReactElement {
  const { getAccessToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [wait, setWait] = useState<LiveStreamWait | null>(null);
  const waitRef = useRef(wait);
  waitRef.current = wait;
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const onWaitRoom = wait !== null && isOnModelRoom(location.pathname, wait.username);

  const startWait = useCallback((next: LiveStreamWait) => {
    setWait(next);
  }, []);

  const clearWait = useCallback(() => {
    setWait(null);
  }, []);

  useEffect(() => {
    if (!wait || onWaitRoom || wait.autoSwitch) {
      return;
    }
    setWait({ ...wait, autoSwitch: true });
  }, [wait, onWaitRoom]);

  useEffect(() => {
    const current = waitRef.current;
    if (!current || isOnModelRoom(location.pathname, current.username)) {
      return;
    }

    const client = createShowRealtimeClient(
      {
        apiBaseUrl,
        getAccessToken: () => getAccessTokenRef.current(),
      },
      {
        onStreamSignal: (signal: StreamSignal) => {
          const pending = waitRef.current;
          if (!pending || signal.showId !== pending.showId) {
            return;
          }
          if (signal.kind !== 'publisher.ready' && signal.kind !== 'offer') {
            return;
          }
          if (pending.notify) {
            notifyStreamWait(pending);
          }
          const username = pending.username;
          const shouldSwitch = pending.autoSwitch;
          setWait(null);
          if (shouldSwitch) {
            navigate(roomPath(username));
          }
        },
      },
    );
    client.subscribe({ username: current.username, showId: current.showId });

    return () => {
      client.disconnect();
    };
  }, [wait?.showId, wait?.username, onWaitRoom, user?.id, location.pathname, navigate]);

  const value = useMemo<LiveStreamWaitContextValue>(
    () => ({
      wait,
      notifyArmed: Boolean(wait?.notify && onWaitRoom),
      startWait,
      clearWait,
    }),
    [wait, onWaitRoom, startWait, clearWait],
  );

  return <LiveStreamWaitContext.Provider value={value}>{children}</LiveStreamWaitContext.Provider>;
}

export function useLiveStreamWait(): LiveStreamWaitContextValue {
  const context = useContext(LiveStreamWaitContext);
  if (!context) {
    throw new Error('useLiveStreamWait must be used within LiveStreamWaitProvider');
  }
  return context;
}
